import Modifier from 'ember-modifier';
import { registerDestructor } from '@ember/destroyable';
import { later } from '@ember/runloop';
import { getOffset } from 'mewe/utils/elements-utils';
import { desktopMinWidth, appTopPadding, appTopPaddingMobile } from 'mewe/constants-layout';

// Usage:
// <element {{scroll-to}}>
// <element {{scroll-to offsetTop=100 behavior='auto'}}>
// <element {{scroll-to update=this.someValue}}>
// <element {{scroll-to onInsert=false mobileOnly=false}}>
// <element {{scroll-to disabled=true}}>
//
// Named args:
// - offsetTop: how close to the top should the element be scrolled to (default: calculate header height)
// - behavior: scroll behavior - 'smooth', 'auto', or 'instant' (default: 'smooth')
// - update: any value that when changed triggers the scroll
// - onInsert: boolean, whether to scroll when element is inserted (default: true)
// - mobileOnly: boolean, only scroll on mobile devices (width < 910px) (default: false)
// - disabled: boolean, prevents scroll from happening even when triggered (default: false)
export default class ScrollToModifier extends Modifier {
  element = null;
  offsetTop = 80;
  behavior = 'smooth';
  update = null;
  onInsert = true;
  mobileOnly = false;
  disabled = false;
  _lastUpdateValue = null;
  delay = 0;

  constructor() {
    super(...arguments);
    registerDestructor(this, () => this.teardown());
  }

  modify(element, positionalArgs = [], namedArgs = {}) {
    this.element = element;
    this.offsetTop = namedArgs.offsetTop ??
      (window.innerWidth > desktopMinWidth ? appTopPadding : appTopPaddingMobile);
    this.behavior = namedArgs.behavior ?? 'smooth';
    this.update = namedArgs.update;
    this.onInsert = namedArgs.onInsert ?? true;
    this.mobileOnly = namedArgs.mobileOnly ?? false;
    this.disabled = namedArgs.disabled ?? false;
    this.delay = namedArgs.delay ?? 0;

    // Handle initial scroll on insert
    if (this.onInsert && this._lastUpdateValue === null) {
      this.performScroll(true);
    }

    // Handle update-triggered scroll
    if (this.update !== undefined && this.update !== this._lastUpdateValue) {
      this._lastUpdateValue = this.update;
      this.performScroll();
    }
  }

  performScroll(onInsert = false) {
    if (!this.element) return;

    // Check if disabled
    if (this.disabled) {
      return;
    }

    // Check mobile-only condition
    if (this.mobileOnly && window.innerWidth >= 910) {
      return;
    }

    const delay = onInsert ? this.delay : 0;

    later(() => {
      const offset = getOffset(this.element);
      window.scrollTo({
        top: offset.top - this.offsetTop,
        behavior: this.behavior
      });
    }, delay);
  }

  teardown() {
    // Cleanup if needed
    this.element = null;
  }
}
